import {
  type FilterLegacy,
  FilterTypeLegacy,
  type ActiveCohortBuilderItemLegacy,
  type InputFieldConfig,
  type InputOptionContextDataLegacy,
} from '@verantos-dev/types-cohorts';
import {
  Simple_eventContext,
  Condition_occurrence_event_criteriaContext,
  Drug_exposure_event_criteriaContext,
  Measurement_event_criteriaContext,
  Observation_event_criteriaContext,
  Procedure_occurrence_event_criteriaContext,
  Visit_occurrence_event_criteriaContext,
} from '@verantos-dev/util-cohort-builder-typescript-runtime';

import { getNextFilters } from './getNextFilters';

export const getActiveCohortBuilderItemStateForSimpleEvent = (
  simpleEventsContext: Simple_eventContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // Map the simple event context to the options

  const conditionOccurrenceContext = simpleEventsContext
    ?.condition_occurrence_event()
    ?.condition_occurrence_event_definition()
    ?.condition_occurrence_event_criteria();
  if (conditionOccurrenceContext) {
    return getConditionOccurrenceContext(
      conditionOccurrenceContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  const drugExposureContext = simpleEventsContext
    ?.drug_exposure_event()
    ?.drug_exposure_event_definition()
    ?.drug_exposure_event_criteria();

  if (drugExposureContext) {
    return getDrugExposureContext(
      drugExposureContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  const measurementContext = simpleEventsContext
    ?.measurement_event()
    ?.measurement_event_definition()
    ?.measurement_event_criteria();
  if (measurementContext) {
    return getMeasurementContext(
      measurementContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  const observationContext = simpleEventsContext
    ?.observation_event()
    ?.observation_event_definition()
    ?.observation_event_criteria();
  if (observationContext) {
    return getObservationContext(
      observationContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  const procedureOccurrenceContext = simpleEventsContext
    ?.procedure_occurrence_event()
    ?.procedure_occurrence_event_definition()
    ?.procedure_occurrence_event_criteria();
  if (procedureOccurrenceContext) {
    return getProcedureOccurrenceContext(
      procedureOccurrenceContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  const visitOccurrenceContext = simpleEventsContext
    ?.visit_occurrence_event()
    ?.visit_occurrence_event_definition()
    ?.visit_occurrence_event_criteria();
  if (visitOccurrenceContext) {
    return getVisitOccurrenceContext(
      visitOccurrenceContext,
      codeCompletionOptions,
      nextActiveCohortBuilderItem,
      inputOptionContextData
    );
  }

  return nextActiveCohortBuilderItem;
};

const getConditionOccurrenceContext = (
  conditionOccurrenceContext: Condition_occurrence_event_criteriaContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // if no filters exists, return the simple event as is
  const conditionOccurrenceEventFilters =
    conditionOccurrenceContext.condition_occurrence_event_filter();

  if (conditionOccurrenceEventFilters) {
    /** Find conditionOccurrence filter sets */

    const { conceptFilters, numericFilters, relatedEntityFilters, uniquenessFilters } =
      nextActiveCohortBuilderItem;
    // If concept filters exist, update the simple event input fields
    let nextConceptFilters: FilterLegacy[] = [];
    let nextConceptInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (conceptFilters.length > 0) {
      const conceptFilterContexts = conceptFilters.map((_, index) => {
        const filter = conditionOccurrenceContext.condition_occurrence_event_filter(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.CONCEPT,
        conceptFilters,
        conceptFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextConceptFilters = nextFilters;
      nextConceptInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If numeric filters exist, update the simple event input fields
    let nextNumericFilters: FilterLegacy[] = [];

    let nextNumericInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (numericFilters.length > 0) {
      const numericFilterContexts = numericFilters.map((_, index) => {
        const filter = conditionOccurrenceContext.condition_occurrence_event_filter(
          index + conceptFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.NUMERIC,
        numericFilters,
        numericFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextNumericFilters = nextFilters;
      nextNumericInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If relatedEntity filters exist, update the simple event input fields
    let nextRelatedEntityFilters: FilterLegacy[] = [];
    let nextRelatedEntityInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (relatedEntityFilters && relatedEntityFilters.length > 0) {
      const relatedEntityFilterContexts = relatedEntityFilters.map((_, index) => {
        const filter = conditionOccurrenceContext.condition_occurrence_event_filter(
          index + conceptFilters.length + numericFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.RELATED_ENTITY,
        relatedEntityFilters,
        relatedEntityFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextRelatedEntityFilters = nextFilters;
      nextRelatedEntityInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If uniqueness filters exist, update the simple event input fields
    let nextUniquenessFilters: FilterLegacy[] = [];
    let nextUniquenessInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (uniquenessFilters && uniquenessFilters.length > 0) {
      const uniquenessFilterContexts = uniquenessFilters.map((_, index) => {
        const filter = conditionOccurrenceContext.condition_occurrence_event_filter(
          index +
            conceptFilters.length +
            numericFilters.length +
            (relatedEntityFilters ? relatedEntityFilters.length : 0)
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.UNIQUENESS,
        uniquenessFilters,
        uniquenessFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextUniquenessFilters = nextFilters;
      nextUniquenessInputFieldConfigMap = nextInputFieldConfigMap;
    }

    return {
      ...nextActiveCohortBuilderItem,
      conceptFilters: nextConceptFilters,
      numericFilters: nextNumericFilters,
      relatedEntityFilters: nextRelatedEntityFilters,
      uniquenessFilters: nextUniquenessFilters,
      inputFieldConfigMap: {
        ...nextConceptInputFieldConfigMap,
        ...nextNumericInputFieldConfigMap,
        ...nextRelatedEntityInputFieldConfigMap,
        ...nextUniquenessInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};

const getDrugExposureContext = (
  drugExposureContext: Drug_exposure_event_criteriaContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // if no filters exists, return the simple event as is
  const drugExposureEventFilters = drugExposureContext.drug_exposure_event_filter();

  if (drugExposureEventFilters) {
    /** Find drugExposure filter sets */

    const { conceptFilters, numericFilters, relatedEntityFilters, uniquenessFilters } =
      nextActiveCohortBuilderItem;

    // If concept filters exist, update the simple event input fields
    let nextConceptFilters: FilterLegacy[] = [];
    let nextConceptInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (conceptFilters.length > 0) {
      const conceptFilterContexts = conceptFilters.map((_, index) => {
        const filter = drugExposureContext.drug_exposure_event_filter(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });

      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.CONCEPT,
        conceptFilters,
        conceptFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextConceptFilters = nextFilters;
      nextConceptInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If numeric filters exist, update the simple event input fields
    let nextNumericFilters: FilterLegacy[] = [];
    let nextNumericInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (numericFilters.length > 0) {
      const numericFilterContexts = numericFilters.map((_, index) => {
        const filter = drugExposureContext.drug_exposure_event_filter(
          index + conceptFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.NUMERIC,
        numericFilters,
        numericFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextNumericFilters = nextFilters;
      nextNumericInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If related entity filters exist, update the simple event input fields
    let nextRelatedEntityFilters: FilterLegacy[] = [];
    let nextRelatedEntityInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (relatedEntityFilters && relatedEntityFilters.length > 0) {
      const relatedEntityFilterContexts = relatedEntityFilters.map((_, index) => {
        const filter = drugExposureContext.drug_exposure_event_filter(
          index + conceptFilters.length + numericFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.RELATED_ENTITY,
        relatedEntityFilters,
        relatedEntityFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextRelatedEntityFilters = nextFilters;
      nextRelatedEntityInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If uniqueness filters exist, update the simple event input fields
    let nextUniquenessFilters: FilterLegacy[] = [];
    let nextUniquenessInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (uniquenessFilters && uniquenessFilters.length > 0) {
      const uniquenessFilterContexts = uniquenessFilters.map((_, index) => {
        const filter = drugExposureContext.drug_exposure_event_filter(
          index +
            conceptFilters.length +
            numericFilters.length +
            (relatedEntityFilters ? relatedEntityFilters.length : 0)
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.UNIQUENESS,
        uniquenessFilters,
        uniquenessFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextUniquenessFilters = nextFilters;
      nextUniquenessInputFieldConfigMap = nextInputFieldConfigMap;
    }

    return {
      ...nextActiveCohortBuilderItem,
      conceptFilters: nextConceptFilters,
      numericFilters: nextNumericFilters,
      relatedEntityFilters: nextRelatedEntityFilters,
      uniquenessFilters: nextUniquenessFilters,
      inputFieldConfigMap: {
        ...nextConceptInputFieldConfigMap,
        ...nextNumericInputFieldConfigMap,
        ...nextRelatedEntityInputFieldConfigMap,
        ...nextUniquenessInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};

const getMeasurementContext = (
  measurementContext: Measurement_event_criteriaContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // if no filters exists, return the simple event as is
  const measurementEventFilters = measurementContext.measurement_event_filter();

  if (measurementEventFilters) {
    /** Find measurement filter sets */

    const { conceptFilters, numericFilters, relatedEntityFilters, uniquenessFilters } =
      nextActiveCohortBuilderItem;

    // If concept filters exist, update the simple event input fields
    let nextConceptFilters: FilterLegacy[] = [];
    let nextConceptInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (conceptFilters.length > 0) {
      const conceptFilterContexts = conceptFilters.map((_, index) => {
        const filter = measurementContext.measurement_event_filter(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.CONCEPT,
        conceptFilters,
        conceptFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextConceptFilters = nextFilters;
      nextConceptInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If numeric filters exist, update the simple event input fields
    let nextNumericFilters: FilterLegacy[] = [];
    let nextNumericInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (numericFilters.length > 0) {
      const numericFilterContexts = numericFilters.map((_, index) => {
        const filter = measurementContext.measurement_event_filter(index + conceptFilters.length);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.NUMERIC,
        numericFilters,
        numericFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextNumericFilters = nextFilters;
      nextNumericInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If related entity filters exist, update the simple event input fields
    let nextRelatedEntityFilters: FilterLegacy[] = [];
    let nextRelatedEntityInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (relatedEntityFilters && relatedEntityFilters.length > 0) {
      const relatedEntityFilterContexts = relatedEntityFilters.map((_, index) => {
        const filter = measurementContext.measurement_event_filter(
          index + conceptFilters.length + numericFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.RELATED_ENTITY,
        relatedEntityFilters,
        relatedEntityFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextRelatedEntityFilters = nextFilters;
      nextRelatedEntityInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If uniqueness filters exist, update the simple event input fields
    let nextUniquenessFilters: FilterLegacy[] = [];
    let nextUniquenessInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (uniquenessFilters && uniquenessFilters.length > 0) {
      const uniquenessFilterContexts = uniquenessFilters.map((_, index) => {
        const filter = measurementContext.measurement_event_filter(
          index +
            conceptFilters.length +
            numericFilters.length +
            (relatedEntityFilters ? relatedEntityFilters.length : 0)
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.UNIQUENESS,
        uniquenessFilters,
        uniquenessFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextUniquenessFilters = nextFilters;
      nextUniquenessInputFieldConfigMap = nextInputFieldConfigMap;
    }

    return {
      ...nextActiveCohortBuilderItem,
      conceptFilters: nextConceptFilters,
      numericFilters: nextNumericFilters,
      relatedEntityFilters: nextRelatedEntityFilters,
      uniquenessFilters: nextUniquenessFilters,
      inputFieldConfigMap: {
        ...nextConceptInputFieldConfigMap,
        ...nextNumericInputFieldConfigMap,
        ...nextRelatedEntityInputFieldConfigMap,
        ...nextUniquenessInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};

const getObservationContext = (
  observationContext: Observation_event_criteriaContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // if no filters exists, return the simple event as is
  const observationEventFilters = observationContext.observation_event_filter();

  if (observationEventFilters) {
    /** Find observation filter sets */

    const { conceptFilters, numericFilters, relatedEntityFilters, uniquenessFilters } =
      nextActiveCohortBuilderItem;

    // If concept filters exist, update the simple event input fields
    let nextConceptFilters: FilterLegacy[] = [];
    let nextConceptInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (conceptFilters.length > 0) {
      const conceptFilterContexts = conceptFilters.map((_, index) => {
        const filter = observationContext.observation_event_filter(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.CONCEPT,
        conceptFilters,
        conceptFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextConceptFilters = nextFilters;
      nextConceptInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If numeric filters exist, update the simple event input fields
    let nextNumericFilters: FilterLegacy[] = [];
    let nextNumericInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (numericFilters.length > 0) {
      const numericFilterContexts = numericFilters.map((_, index) => {
        const filter = observationContext.observation_event_filter(index + conceptFilters.length);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.NUMERIC,
        numericFilters,
        numericFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextNumericFilters = nextFilters;
      nextNumericInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If related entity filters exist, update the simple event input fields
    let nextRelatedEntityFilters: FilterLegacy[] = [];
    let nextRelatedEntityInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (relatedEntityFilters && relatedEntityFilters.length > 0) {
      const relatedEntityFilterContexts = relatedEntityFilters.map((_, index) => {
        const filter = observationContext.observation_event_filter(
          index + conceptFilters.length + numericFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.RELATED_ENTITY,
        relatedEntityFilters,
        relatedEntityFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextRelatedEntityFilters = nextFilters;
      nextRelatedEntityInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If uniqueness filters exist, update the simple event input fields
    let nextUniquenessFilters: FilterLegacy[] = [];
    let nextUniquenessInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (uniquenessFilters && uniquenessFilters.length > 0) {
      const uniquenessFilterContexts = uniquenessFilters.map((_, index) => {
        const filter = observationContext.observation_event_filter(
          index +
            conceptFilters.length +
            numericFilters.length +
            (relatedEntityFilters ? relatedEntityFilters.length : 0)
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.UNIQUENESS,
        uniquenessFilters,
        uniquenessFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextUniquenessFilters = nextFilters;
      nextUniquenessInputFieldConfigMap = nextInputFieldConfigMap;
    }

    return {
      ...nextActiveCohortBuilderItem,
      conceptFilters: nextConceptFilters,
      numericFilters: nextNumericFilters,
      relatedEntityFilters: nextRelatedEntityFilters,
      uniquenessFilters: nextUniquenessFilters,
      inputFieldConfigMap: {
        ...nextConceptInputFieldConfigMap,
        ...nextNumericInputFieldConfigMap,
        ...nextRelatedEntityInputFieldConfigMap,
        ...nextUniquenessInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};

const getProcedureOccurrenceContext = (
  procedureOccurrenceContext: Procedure_occurrence_event_criteriaContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // if no filters exists, return the simple event as is
  const drugExposureEventFilters = procedureOccurrenceContext.procedure_occurrence_event_filter();

  if (drugExposureEventFilters) {
    /** Find drugExposure filter sets */

    const { conceptFilters, numericFilters, relatedEntityFilters, uniquenessFilters } =
      nextActiveCohortBuilderItem;

    // If concept filters exist, update the simple event input fields
    let nextConceptFilters: FilterLegacy[] = [];
    let nextConceptInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (conceptFilters.length > 0) {
      const conceptFilterContexts = conceptFilters.map((_, index) => {
        const filter = procedureOccurrenceContext.procedure_occurrence_event_filter(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.CONCEPT,
        conceptFilters,
        conceptFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextConceptFilters = nextFilters;
      nextConceptInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If numeric filters exist, update the simple event input fields
    let nextNumericFilters: FilterLegacy[] = [];
    let nextNumericInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (numericFilters.length > 0) {
      const numericFilterContexts = numericFilters.map((_, index) => {
        const filter = procedureOccurrenceContext.procedure_occurrence_event_filter(
          index + conceptFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.NUMERIC,
        numericFilters,
        numericFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextNumericFilters = nextFilters;
      nextNumericInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If related entity filters exist, update the simple event input fields
    let nextRelatedEntityFilters: FilterLegacy[] = [];
    let nextRelatedEntityInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (relatedEntityFilters && relatedEntityFilters.length > 0) {
      const relatedEntityFilterContexts = relatedEntityFilters.map((_, index) => {
        const filter = procedureOccurrenceContext.procedure_occurrence_event_filter(
          index + conceptFilters.length + numericFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.RELATED_ENTITY,
        relatedEntityFilters,
        relatedEntityFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextRelatedEntityFilters = nextFilters;
      nextRelatedEntityInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If uniqueness filters exist, update the simple event input fields
    let nextUniquenessFilters: FilterLegacy[] = [];
    let nextUniquenessInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (uniquenessFilters && uniquenessFilters.length > 0) {
      const uniquenessFilterContexts = uniquenessFilters.map((_, index) => {
        const filter = procedureOccurrenceContext.procedure_occurrence_event_filter(
          index +
            conceptFilters.length +
            numericFilters.length +
            (relatedEntityFilters ? relatedEntityFilters.length : 0)
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.UNIQUENESS,
        uniquenessFilters,
        uniquenessFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextUniquenessFilters = nextFilters;
      nextUniquenessInputFieldConfigMap = nextInputFieldConfigMap;
    }

    return {
      ...nextActiveCohortBuilderItem,
      conceptFilters: nextConceptFilters,
      numericFilters: nextNumericFilters,
      relatedEntityFilters: nextRelatedEntityFilters,
      uniquenessFilters: nextUniquenessFilters,
      inputFieldConfigMap: {
        ...nextConceptInputFieldConfigMap,
        ...nextNumericInputFieldConfigMap,
        ...nextRelatedEntityInputFieldConfigMap,
        ...nextUniquenessInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};

const getVisitOccurrenceContext = (
  visitOccurrenceContext: Visit_occurrence_event_criteriaContext,
  codeCompletionOptions: string[][],
  nextActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy,
  inputOptionContextData: InputOptionContextDataLegacy
): ActiveCohortBuilderItemLegacy => {
  // if no filters exists, return the simple event as is
  const drugExposureEventFilters = visitOccurrenceContext.visit_occurrence_event_filter();

  if (drugExposureEventFilters) {
    /** Find drugExposure filter sets */

    const { conceptFilters, numericFilters, relatedEntityFilters, uniquenessFilters } =
      nextActiveCohortBuilderItem;

    // If concept filters exist, update the simple event input fields
    let nextConceptFilters: FilterLegacy[] = [];
    let nextConceptInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (conceptFilters.length > 0) {
      const conceptFilterContexts = conceptFilters.map((_, index) => {
        const filter = visitOccurrenceContext.visit_occurrence_event_filter(index);
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.CONCEPT,
        conceptFilters,
        conceptFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextConceptFilters = nextFilters;
      nextConceptInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If numeric filters exist, update the simple event input fields
    let nextNumericFilters: FilterLegacy[] = [];
    let nextNumericInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (numericFilters.length > 0) {
      const numericFilterContexts = numericFilters.map((_, index) => {
        const filter = visitOccurrenceContext.visit_occurrence_event_filter(
          index + conceptFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.NUMERIC,
        numericFilters,
        numericFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextNumericFilters = nextFilters;
      nextNumericInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If related entity filters exist, update the simple event input fields
    let nextRelatedEntityFilters: FilterLegacy[] = [];
    let nextRelatedEntityInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (relatedEntityFilters && relatedEntityFilters.length > 0) {
      const relatedEntityFilterContexts = relatedEntityFilters.map((_, index) => {
        const filter = visitOccurrenceContext.visit_occurrence_event_filter(
          index + conceptFilters.length + numericFilters.length
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.RELATED_ENTITY,
        relatedEntityFilters,
        relatedEntityFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextRelatedEntityFilters = nextFilters;
      nextRelatedEntityInputFieldConfigMap = nextInputFieldConfigMap;
    }

    // If uniqueness filters exist, update the simple event input fields
    let nextUniquenessFilters: FilterLegacy[] = [];
    let nextUniquenessInputFieldConfigMap: Record<string, InputFieldConfig> = {};
    if (uniquenessFilters && uniquenessFilters.length > 0) {
      const uniquenessFilterContexts = uniquenessFilters.map((_, index) => {
        const filter = visitOccurrenceContext.visit_occurrence_event_filter(
          index +
            conceptFilters.length +
            numericFilters.length +
            (relatedEntityFilters ? relatedEntityFilters.length : 0)
        );
        if (!filter) {
          throw new Error('Filter not found');
        }
        return filter;
      });
      const { nextFilters, nextInputFieldConfigMap } = getNextFilters(
        FilterTypeLegacy.UNIQUENESS,
        uniquenessFilters,
        uniquenessFilterContexts,
        codeCompletionOptions,
        inputOptionContextData
      );
      nextUniquenessFilters = nextFilters;
      nextUniquenessInputFieldConfigMap = nextInputFieldConfigMap;
    }

    return {
      ...nextActiveCohortBuilderItem,
      conceptFilters: nextConceptFilters,
      numericFilters: nextNumericFilters,
      relatedEntityFilters: nextRelatedEntityFilters,
      uniquenessFilters: nextUniquenessFilters,
      inputFieldConfigMap: {
        ...nextConceptInputFieldConfigMap,
        ...nextNumericInputFieldConfigMap,
        ...nextRelatedEntityInputFieldConfigMap,
        ...nextUniquenessInputFieldConfigMap,
      },
    };
  }

  return nextActiveCohortBuilderItem;
};
